import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { SSE } from 'sse.js';

import { KimMessagesStreamType, StreamBlock } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';

const RECONNECT_DELAY_MS = 2000;

export interface KimStreamChunk {
  type: KimMessagesStreamType;
  content: StreamBlock;
  timestamp: number;
}

interface UseKimStreamProps {
  matterId: string;
  subsetId: string;
  threadId: string;
  enabled?: boolean;
  initialLastMessageId?: string;
}

export const useKimStream = ({
  matterId,
  subsetId,
  threadId,
  enabled = true,
  initialLastMessageId,
}: UseKimStreamProps) => {
  const [chunks, setChunks] = useState<KimStreamChunk[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const sseRef = useRef<SSE | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const generationRef = useRef(0);

  // Held in a ref so `connect` keeps a stable identity. PropelAuth swaps the
  // authInfo object on every token refresh, which would otherwise tear down and
  // rebuild a perfectly healthy stream.
  const authInfo = useAuthInfo();
  const authInfoRef = useRef(authInfo);
  authInfoRef.current = authInfo;

  // Resolved per connection attempt rather than captured once: a thread stays
  // open far longer than an access token lives, so a token read at mount is
  // already stale by the time a reconnect needs it.
  const getFreshAccessToken = useCallback(async () => {
    const auth = authInfoRef.current;
    const token = await auth.tokens.getAccessToken();
    if (token !== undefined) return token;

    // Undefined means PropelAuth's refresh failed (e.g. waking from sleep).
    await auth.refreshAuthInfo();
    return auth.tokens.getAccessToken();
  }, []);

  const buildUrl = useCallback(
    (lastMessageId: string | null) => {
      const base = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${threadId}/stream`;
      if (lastMessageId) {
        return `${base}?lastMessageId=${encodeURIComponent(lastMessageId)}`;
      }
      return base;
    },
    [matterId, subsetId, threadId],
  );

  const connect = useCallback(
    async (generation: number) => {
      // A thread switch, unmount or manual disconnect bumps the generation, so
      // an attempt still awaiting a token can't open a stream for the old thread.
      const isCurrent = () => isMountedRef.current && enabled && generationRef.current === generation;

      if (!isCurrent()) return;

      // Close any existing connection
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }

      const scheduleReconnect = () => {
        if (!isCurrent()) return;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          void connect(generation);
        }, RECONNECT_DELAY_MS);
      };

      try {
        const accessToken = await getFreshAccessToken();

        if (!isCurrent()) return;

        if (accessToken === undefined) {
          setIsConnected(false);
          scheduleReconnect();
          return;
        }

        const url = buildUrl(lastMessageIdRef.current);

        const eventSource = new SSE(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        eventSource.onmessage = (event: any) => {
          if (!isCurrent()) return;

          try {
            if (!event.data || !event.data.trim()) return;

            const data = JSON.parse(event.data);
            lastMessageIdRef.current = String(data.timestamp);
            setChunks((prev) => [...prev, data as KimStreamChunk]);
          } catch (error) {
            console.error('Failed to parse Kim stream event:', error);
          }
        };

        eventSource.onopen = () => {
          if (isCurrent()) {
            setIsConnected(true);
          }
        };

        eventSource.onerror = (event: any) => {
          if (!isCurrent()) return;

          setIsConnected(false);

          // A 401 means the token died mid-stream. Refresh before retrying,
          // otherwise every reconnect replays the same rejected token and the
          // stream never recovers.
          if (event?.responseCode === 401) {
            void authInfoRef.current.refreshAuthInfo().finally(scheduleReconnect);
            return;
          }

          // Auto-reconnect with lastMessageId after a short delay
          scheduleReconnect();
        };

        sseRef.current = eventSource;
      } catch (error) {
        console.error('Failed to setup Kim stream event source:', error);
        scheduleReconnect();
      }
    },
    [enabled, buildUrl, getFreshAccessToken],
  );

  useEffect(() => {
    isMountedRef.current = true;
    setChunks([]);
    lastMessageIdRef.current = initialLastMessageId ?? null;
    const generation = ++generationRef.current;

    if (enabled && matterId && subsetId && threadId) {
      void connect(generation);
    }

    return () => {
      isMountedRef.current = false;
      generationRef.current++;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      setIsConnected(false);
    };
  }, [enabled, matterId, subsetId, threadId, connect, initialLastMessageId]);

  const disconnect = useCallback(() => {
    generationRef.current++;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const reset = useCallback(() => {
    disconnect();
    lastMessageIdRef.current = null;
    setChunks([]);
  }, [disconnect]);

  const content = chunks
    .map((c) => {
      if (!c.content) return `[no content field - keys: ${Object.keys(c).join(',')}]`;

      if (c.content.type === 'text') return c.content.value;
      if (c.content.type === 'start') return '';
      if (c.content.type === 'end') return '';
      if (c.content.type === 'reference') return `[${c.content.type}:${c.content.id}]`;
    })
    .join(' ');

  return { chunks, content, isConnected, disconnect, reset };
};
